import { ApolloClient, ApolloQueryResult, NormalizedCacheObject } from '@apollo/client';
import Bugsnag from '@bugsnag/js';
import { getProductGroupConfigByKey } from '@99designs/feature-product-details';
import { handleQuery } from '@99designs/graph-utils';
import { ContestPriceDocument } from '../graphql/ProductLanding.generated';
import type { CurrencyCode } from '@99designs/i18n';
import type {
    ContestPriceQuery,
    ProductContestFragment,
    ProductFixedPriceProjectFragment,
} from '../graphql/ProductLanding.generated';
import type { ProductLandingGroupKeys } from '../lib/productLandingGroups';
import type { ProductGroupPrice } from '../types';

export type Price = {
    currency: CurrencyCode;
    amountInCents: number;
};

export async function getProductGroupPrices(
    client: ApolloClient<NormalizedCacheObject>,
    productGroupKeys: ProductLandingGroupKeys
): Promise<ProductGroupPrice[]> {
    const productGroupName = productGroupKeys.map((productkey) => {
        return getProductGroupConfigByKey(productkey).name;
    });

    const inputQuery = {
        productGroupNames: [productGroupName],
    };
    const { data, error } = await fetchproductGroupPrices(client, inputQuery);

    if (error) {
        return [];
    }

    if (!data || !data.products) {
        Bugsnag.notify(
            new Error('Feature Product Landing - Product pricing not found'),
            (event) => {
                event.context = `Product pricing not found for ${JSON.stringify(inputQuery)}`;
            }
        );
        return [];
    }

    function isUsableProduct(
        product:
            | ProductFixedPriceProjectFragment
            | ProductContestFragment
            | { __typename: 'ProductQuotedProject' }
    ): product is ProductFixedPriceProjectFragment | ProductContestFragment {
        return product.__typename !== 'ProductQuotedProject';
    }

    return data.products.filter(isUsableProduct).map((product) => {
        return {
            key: product.category,
            prices: product.pricing.amounts as Price[],
        };
    });
}

function fetchproductGroupPrices(
    client: ApolloClient<NormalizedCacheObject>,
    inputQuery: { productGroupNames: string[][] }
): Promise<ApolloQueryResult<ContestPriceQuery>> {
    return handleQuery<ContestPriceQuery>(client, {
        query: ContestPriceDocument,
        variables: inputQuery,
    });
}
